// ════════════════════════════════════════════════════════════════════════
// MESURES — test_results → moteurs (scoring de phases, MRSS 2.0, RTR gate)
//
// Module PUR (aucune dépendance React / réseau / DOM). C'est le seul endroit
// qui sait comment une ligne de test_results alimente chaque moteur.
// Les trois vues lisent donc la même source : une saisie = une ligne.
//
// ── Vocabulaire ─────────────────────────────────────────────────────────
//   measures : { [test_id]: valeur }   valeur = { v, trials?, items?, ov? }
//              (forme stockée dans test_results.valeur). Seuls les tests
//              réellement mesurés y figurent : une clé absente = NON_MESURE.
//   ctx      : { dateOp, date }        (date op patient, date de session)
//
// ── Règle de partage ────────────────────────────────────────────────────
// Une variable RTR/MRSS réutilise une ligne V1.13 existante UNIQUEMENT quand
// c'est la même mesure (même test, même unité) — cf. test_definitions
// .variable_document. Sinon : ligne dédiée rtr_* / mrss_* (migration
// Supabase/20261015_switch_app_to_test_results.sql). Les lectures avec
// conversion (`read`) ou dérivées (`derive`) sont en lecture seule dans le
// panneau RTR : la saisie se fait dans l'onglet d'origine.
// ════════════════════════════════════════════════════════════════════════

import { computeMrss } from "./mrssEngine";

export const TRANSITION_KEYS = ["preop→p1", "p1→p2", "p2→p3", "p3→p4", "p4→p5"];

// Phase de session (test_sessions.phase, 0-5) à laquelle un test est rattaché :
// la phase de DÉPART de la transition évaluée (p2→p3 → 2).
export function phaseOfTest(testId) {
  const tr = TRANSITION_KEYS.findIndex(t => testId.startsWith(`${t}_`));
  if (tr >= 0) return tr;
  if (testId.startsWith("rtr_run_") || testId === "rtr_quad_epic") return 2;
  if (testId.startsWith("rtr_sport_") || testId.startsWith("mrss_")) return 3;
  return null;
}

// ── Index : dernière valeur par test (date effective = date_override ?? date_session) ──
export function buildMeasureIndex(sessions = []) {
  const idx = {};
  for (const s of sessions) {
    for (const r of s.test_results || []) {
      const date = r.date_override || s.date_session;
      const createdAt = r.created_at || "";
      const cur = idx[r.test_id];
      if (!cur || date > cur.date || (date === cur.date && createdAt >= cur.createdAt)) {
        idx[r.test_id] = { valeur: r.valeur || {}, date, sessionId: s.id, phase: s.phase, createdAt };
      }
    }
  }
  return idx;
}

// measures conserve la date effective de chaque mesure (champ `date`) : nécessaire
// à la règle de fraîcheur. Ce champ n'est jamais écrit dans test_results.valeur.
export function measuresFromIndex(idx) {
  const out = {};
  for (const [k, e] of Object.entries(idx)) out[k] = { ...e.valeur, date: e.date };
  return out;
}

// ── Fraîcheur des mesures (règle validée par Jérôme, 15/10/2026) ──────────
//   Force, hop tests, isocinétique : 6 semaines (batterie mensuelle ; fenêtre
//     « ≥ 2 mesures en 6 semaines » de EN_PROGRESSION)
//   Épanchement, douleur, ROM : 7 jours (état du genou, testé à chaque séance)
//   Dérobement : pas de fenêtre — événements datés de giving_way_events
//   Question « Dérobement depuis le dernier bilan ? » (V1.14) : 0 jour — seule la
//     réponse du bilan évalué compte ; une réponse d'un bilan antérieur = sans réponse
// Une mesure périmée ne compte pas comme 0 : elle passe en NON_MESURE (retirée du
// numérateur et du dénominateur) et, pour un hurdle bloquant, en À RETESTER.
// Tests non listés : AUCUNE fenêtre (hypothèse en attente de validation —
// questionnaires, délais, équilibre/SEBT, qualité de mouvement, tolérance
// course/COD, condition physique, critères administratifs).
export const FRESHNESS_RULES = {
  force_hop_isok: {
    maxDays: 42, label: "Force / hop tests / isocinétique — 6 semaines",
    ids: [
      "preop→p1_quad_lsi", "preop→p1_ij_lsi", "preop→p1_hop_preop",
      "p2→p3_sl_rise", "p2→p3_sl_bridge", "p2→p3_sl_calf", "p2→p3_side_bridge", "p2→p3_goblet", "p2→p3_back_lunge",
      "p2→p3_bulg_squat", "p2→p3_sl_deadlift", "p2→p3_quad_iso", "p2→p3_ij_iso", "p2→p3_load_1_5", "p2→p3_glute",
      "p2→p3_pogos", "p2→p3_sl_forward_hop", "p2→p3_triple_hop_rtr", "p2→p3_crossover_rtr", "p2→p3_vertical_hop_rtr",
      "p2→p3_rep_fwd_hop", "p2→p3_rep_vert_hop", "p2→p3_sl_landing", "p2→p3_isok_rtr",
      "p3→p4_sl_rise_p3", "p3→p4_sl_bridge_p3", "p3→p4_sl_calf_p3", "p3→p4_side_bridge_p3", "p3→p4_leg_press_p3",
      "p3→p4_squat_1rm_p3", "p3→p4_plyo_bip", "p3→p4_single_hop_rts", "p3→p4_triple_hop_rts", "p3→p4_crossover_rts",
      "p3→p4_sidehop30_rts", "p3→p4_vertical_hop_rts", "p3→p4_sl_landing_rts", "p3→p4_isok_rts",
      "p3→p4_r2train_quad", "p3→p4_r2train_ij", "p3→p4_r2train_hop", "p3→p4_r2train_triple", "p3→p4_r2train_vert",
      "p3→p4_r2unrestr_quad", "p3→p4_r2unrestr_hop", "p3→p4_r2unrestr_side",
      "rtr_quad_epic", "rtr_run_pogo_sl_reps", "rtr_run_hop_vh_tolere", "rtr_run_cone_hop", "rtr_run_tuck_jump_sl", "rtr_run_dj_rsi_lsi",
      "rtr_sport_hip_abd", "rtr_sport_hip_add", "rtr_sport_hip_re", "rtr_sport_calf_mvc", "rtr_sport_quad_isok", "rtr_sport_ham_isok",
      "rtr_sport_ratio_add_abd", "rtr_sport_cmj_height", "rtr_sport_sl_hop_distance", "rtr_sport_drop_jump", "rtr_sport_contact_time",
      "rtr_sport_rsi", "rtr_sport_symetrie_lsi", "rtr_run_dj_rsi_sl", "p4→p5_quad_iso",
      "mrss_f_single_hop", "mrss_f_triple_hop", "mrss_f_crossover_hop", "mrss_f_side_hop",
    ],
  },
  etat_genou: {
    maxDays: 7, label: "Épanchement / douleur / ROM — 7 jours",
    ids: [
      "preop→p1_ext", "preop→p1_flex", "preop→p1_epanch",
      "p1→p2_ext", "p1→p2_flex", "p1→p2_epanch", "p1→p2_douleur",
      "p2→p3_douleur", "p2→p3_epanch", "p2→p3_ext", "p2→p3_flex_pct",
      "p3→p4_douleur_p3", "p3→p4_epanch_p3", "p3→p4_ext_p3", "p3→p4_flex_p3",
      "rtr_run_epanch_drills", "rtr_run_flexion_deg", "rtr_run_douleur_drills", "rtr_run_reaction_j1", "rtr_sport_rom",
      "p4→p5_epanch",
    ],
  },
  question_bilan: {
    maxDays: 0, label: "Question obligatoire du bilan — réponse du jour",
    ids: ["p1→p2_derob_bilan", "p2→p3_derob_bilan", "p3→p4_derob_bilan", "p4→p5_derob_bilan"],
  },
};
const FRESHNESS_BY_ID = (() => {
  const o = {};
  for (const [cat, r] of Object.entries(FRESHNESS_RULES)) for (const id of r.ids) o[id] = { category: cat, maxDays: r.maxDays, label: r.label };
  return o;
})();
export const freshnessRule = testId => FRESHNESS_BY_ID[testId] || null;

const dayDiff = (from, to) => Math.floor((Date.parse(to) - Date.parse(from)) / 86400000);

// → { fresh: measures sans les mesures périmées, stale: { [testId]: { date, ageDays, maxDays } } }
// refDate = date de la session évaluée. Une mesure postérieure à refDate n'est jamais périmée.
export function splitFresh(m = {}, refDate) {
  const fresh = {}, stale = {};
  for (const [k, e] of Object.entries(m)) {
    const rule = freshnessRule(k);
    const age = rule && e?.date && refDate ? dayDiff(e.date, refDate) : null;
    if (age !== null && Number.isFinite(age) && age > rule.maxDays) stale[k] = { date: e.date, ageDays: age, maxDays: rule.maxDays };
    else fresh[k] = e;
  }
  return { fresh, stale };
}

// ── Dérobements (giving_way_events) — pas de fenêtre de fraîcheur ──────────
// V1.14, convention clinique N4 (J. Tubez, 22/10/2026) : post-op = 0 toléré, comptés
// depuis l'ENTRÉE DANS LA PHASE EN COURS = dernier GO (ou GO_CONDITIONNEL) de la
// transition qui y mène ; en P1 (ou sans décision enregistrée) depuis la date d'op.
// Comparaison >= : un épisode le jour de la borne compte.
// Question obligatoire du bilan : une ligne par transition (la session dépend du préfixe).
export const GIVING_WAY_QUESTION = { "p1→p2": "p1→p2_derob_bilan", "p2→p3": "p2→p3_derob_bilan", "p3→p4": "p3→p4_derob_bilan", "p4→p5": "p4→p5_derob_bilan" };
// Décisions qui font ENTRER dans la phase évaluée par la transition (clé).
// « RTR-SPORT » couvre les décisions enregistrées « RTR-SPORT:<profil> ».
export const PHASE_ENTRY_GATES = { "p1→p2": [], "p2→p3": ["p1→p2"], "p3→p4": ["RTR-RUN", "p2→p3"], "p4→p5": ["RTR-SPORT", "p3→p4"] };
const ENTRY_DECISIONS = ["GO", "GO_CONDITIONNEL"];
const gateMatches = (gate = "", g) => gate === g || gate.startsWith(`${g}:`);

// Date d'entrée dans la phase évaluée par la transition (dernier GO / GO_CONDITIONNEL), sinon null
export function phaseEntryDate(tr, { decisions = [], date } = {}) {
  const gates = PHASE_ENTRY_GATES[tr] || [];
  return decisions
    .filter(d => gates.some(g => gateMatches(d.gate, g)) && ENTRY_DECISIONS.includes(d.decision) && (!date || d.date_decision <= date))
    .map(d => d.date_decision).sort().pop() || null;
}

export function givingWayWindowStart(tr, { decisions = [], dateOp, date } = {}) {
  return phaseEntryDate(tr, { decisions, date }) || dateOp || null;
}

export function countGivingWay(events = [], { since, until } = {}) {
  return events.filter(e => (!since || e.date_event >= since) && (!until || e.date_event <= until)).length;
}

// Pré-op (≤ 1 depuis la blessure, FIT2000) : informatif, jamais le gating post-op.
// Pas de date de blessure en base → tous les événements antérieurs à la date d'op.
export function countGivingWayPreOp(events = [], dateOp) {
  return dateOp ? events.filter(e => e.date_event < dateOp).length : 0;
}

// Valeur du hurdle « dérobement post-op » attendue par rtrEngine (direction zero_giving_way)
export function givingWayHurdleValue(m, ctx, tr) {
  return { answer: bool(val(m, GIVING_WAY_QUESTION[tr])), count: ctx.givingWayCount ?? 0, since: ctx.givingWaySince || null };
}

// ── Helpers de lecture ────────────────────────────────────────────────
const isNum = v => v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v));
export const num = v => (isNum(v) ? Number(v) : undefined);
export const bool = v => (v === true || v === 1 ? true : v === false || v === 0 ? false : undefined);
const round1 = x => Math.round(x * 10) / 10;
const val = (m, id) => m[id]?.v;

// LSI = côté opéré / côté sain × 100 (non borné à 100, contrairement à l'affichage CRITERIA)
export function lsiFromPair(p) {
  const h = num(p?.h), i = num(p?.i);
  if (!h || !i) return undefined;
  return round1((i / h) * 100);
}

// LSI isocinétique à 60°/s depuis la saisie "isok" du scoring (side = côté opéré)
export function isokLsi(iso, muscle = "quad") {
  if (!iso) return undefined;
  const left = (iso.side || "left") === "left";
  const [sain, lese] = muscle === "quad"
    ? (left ? [iso.qR60, iso.qL60] : [iso.qL60, iso.qR60])
    : (left ? [iso.ijR60, iso.ijL60] : [iso.ijL60, iso.ijR60]);
  return lsiFromPair({ h: sain, i: lese });
}

// Sélecteur CRITERIA épanchement ["2+","1+","Trace","0 (absent)"] → grade RTR (0 absent … 3 = 2+)
const EPANCH_IDX_TO_GRADE = [3, 2, 1, 0];
const epanchGrade = v => (isNum(v) ? EPANCH_IDX_TO_GRADE[Number(v)] : undefined);
const CROSSLEY = ["Pauvre (Poor)", "Passable (Fair)", "Bon (Good)"];
const repsOk = v => (isNum(v) ? Number(v) >= 15 : undefined);
const pogoReps = m => latestOf(m, [["p2→p3_pogos", v => (num(v) === 1 ? undefined : num(v))], ["rtr_run_pogo_sl_reps", num]]);

export function weeksSinceOp(ctx = {}) {
  if (!ctx.dateOp || !ctx.date) return undefined;
  const d = (new Date(ctx.date) - new Date(ctx.dateOp)) / 86400000;
  return Number.isFinite(d) && d >= 0 ? Math.floor(d / 7) : undefined;
}
export function monthsSinceOp(ctx = {}) {
  if (!ctx.dateOp || !ctx.date) return undefined;
  const a = new Date(ctx.dateOp), b = new Date(ctx.date);
  if (!Number.isFinite(a.getTime()) || !Number.isFinite(b.getTime()) || b < a) return undefined;
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) months -= 1; // mois complets uniquement (conservateur)
  return months;
}

// Semaines écoulées depuis une date (délais intra-phase : depuis l'entrée dans la phase)
export function weeksSince(from, date) {
  if (!from || !date) return undefined;
  const d = (new Date(date) - new Date(from)) / 86400000;
  return Number.isFinite(d) && d >= 0 ? Math.floor(d / 7) : undefined;
}

// Mesure la plus récente parmi plusieurs lignes (même mesure saisie à deux endroits) :
// candidates = [[testId, read]] ; à date égale, l'ordre de la liste départage (ligne de phase d'abord).
function latestOf(m, candidates) {
  let best;
  for (const [id, read] of candidates) {
    const e = m[id];
    if (!e) continue;
    const v = read(e.v);
    if (v === undefined) continue;
    if (!best || (e.date || "") > best.date) best = { v, date: e.date || "" };
  }
  return best?.v;
}

// ── %EPIC automatique (V1.15, D7) ─────────────────────────────────────
// %EPIC = force du côté OPÉRÉ aujourd'hui / force du côté SAIN mesurée AVANT l'opération × 100.
// Même outil, même position, même unité que la mesure de suivi. Sans baseline pré-op → NON_MESURE.
export const EPIC_BASELINE = { quad: "preop→p1_epic_quad_iso" };
export function epicPct(m, currentId, baselineId = EPIC_BASELINE.quad) {
  const cur = num(val(m, currentId)?.i), base = num(val(m, baselineId));
  return cur && base ? round1((cur / base) * 100) : undefined;
}

// ── Carnet de séances (patients.session_history) — V1.15, D4/D5 ─────────
// RPE stable : >= 2 séances notées dans les 14 jours précédant le bilan et écart max-min
// <= 1 point (CR10) — convention à valider. Sinon : réponse manuelle du kiné.
export function rpeStableFromLog(log = [], date) {
  if (!date) return undefined;
  const rpes = log.filter(s => s?.date && s.date <= date && dayDiff(s.date, date) < 14).map(s => num(s.rpe)).filter(x => x > 0);
  if (rpes.length < 2) return undefined;
  return Math.max(...rpes) - Math.min(...rpes) <= 1;
}
// Adhérence : séances notées (cabinet, domicile, terrain) sur les 28 jours précédant le bilan
// ÷ (séances prescrites par semaine × 4). Carnet vide ou fréquence absente → saisie manuelle.
export function adherenceFromLog(log = [], perWeek, date) {
  const f = num(perWeek);
  if (!date || !f) return undefined;
  const n = log.filter(s => s?.date && s.date <= date && dayDiff(s.date, date) < 28).length;
  return n ? Math.min(100, Math.round((n / (f * 4)) * 100)) : undefined;
}

// ── RTR-SPORT : valeurs reprises des tests P3→P4 (V1.15, D1 appliqué au gate T6) ──
// Symétrie : LSI le plus bas parmi les mesures symétriques P3→P4 réellement saisies.
export const SPORT_SYMMETRY_SOURCES = [
  ["Isocinétique quadriceps", m => isokLsi(val(m, "p3→p4_isok_rts"), "quad")],
  ["Isocinétique ischio-jambiers", m => isokLsi(val(m, "p3→p4_isok_rts"), "ham")],
  ["Single hop", m => lsiFromPair(val(m, "p3→p4_single_hop_rts"))],
  ["Triple hop", m => lsiFromPair(val(m, "p3→p4_triple_hop_rts"))],
  ["Crossover hop", m => lsiFromPair(val(m, "p3→p4_crossover_rts"))],
  ["Side hop 30\"", m => lsiFromPair(val(m, "p3→p4_sidehop30_rts"))],
  ["Vertical hop", m => lsiFromPair(val(m, "p3→p4_vertical_hop_rts"))],
];
export function minSymmetry(m) {
  const vals = SPORT_SYMMETRY_SOURCES.map(([label, f]) => ({ label, lsi: f(m) })).filter(x => x.lsi !== undefined);
  return vals.length ? vals.reduce((a, b) => (b.lsi < a.lsi ? b : a)) : undefined;
}
// Couple isocinétique de pointe à 60°/s, côté opéré, en % du poids de corps (Nm/kg × 100)
export function isokPctBw(iso, muscle = "quad") {
  if (!iso || !num(iso.weight)) return undefined;
  const left = (iso.side || "left") === "left";
  const lese = muscle === "quad" ? (left ? iso.qL60 : iso.qR60) : (left ? iso.ijL60 : iso.ijR60);
  return num(lese) ? round1((Number(lese) / Number(iso.weight)) * 100) : undefined;
}
const isokSymmetric = muscle => m => {
  const iso = val(m, "p3→p4_isok_rts"), abs = isokPctBw(iso, muscle), lsi = isokLsi(iso, muscle);
  return abs !== undefined || lsi !== undefined ? { abs, lsi } : undefined;
};
// Hop / CMJ côté opéré vs la même mesure côté SAIN avant l'opération (baseline EPIC)
export function vsPreopBaseline(m, kind) {
  const cur = num(val(m, kind === "hop" ? "p3→p4_single_hop_rts" : "p3→p4_vertical_hop_rts")?.i);
  const base = kind === "hop" ? num(val(m, "preop→p1_hop_preop")?.h) : num(val(m, "preop→p1_epic_sl_cmj"));
  return cur && base ? { cur, base, meets: cur >= base, pct: round1((cur / base) * 100) } : undefined;
}

// Force dynamomètre (% PC) : %EPIC calculé = valeur côté opéré / baseline côté sain pré-op
// (V1.15, 24/10/2026) ; sans baseline, le %EPIC saisi à la main reste lu.
export function withPreopEpic(m, testId, baselineId) {
  const v = val(m, testId);
  if (!v || typeof v !== "object") return v;
  const abs = num(v.abs), base = num(val(m, baselineId));
  return abs && base ? { ...v, epic: round1((abs / base) * 100) } : v;
}

// ── RTR-RUN (gate T4, P2→P3) ──────────────────────────────────────────
// Chaque clé = identifiant attendu par rtrEngine (src/rtrRules.js).
export const RTR_RUN_MAP = {
  // Délai : toujours calculé depuis la date d'opération (V1.15, D8) — plus de curseur
  delai_postop: { from: ["date d'opération"], derive: (m, ctx) => weeksSinceOp(ctx), auto: true },
  epanchement_drills: { testId: "rtr_run_epanch_drills" },
  extension: { testId: "p2→p3_ext", read: v => (isNum(v) ? Number(v) <= 0 : undefined) },
  // Flexion : pré-remplie depuis P2→P3 « Flexion vs côté sain » (degrés, côté opéré = i,
  // même mesure — V1.13 cle_production H4 = p2→p3_flex_pct), sinon saisie dédiée du gate.
  flexion: {
    from: ["p2→p3_flex_pct", "rtr_run_flexion_deg"],
    derive: m => latestOf(m, [["p2→p3_flex_pct", v => num(v?.i)], ["rtr_run_flexion_deg", num]]),
  },
  douleur_course: { testId: "rtr_run_douleur_drills" },
  reaction_j1: { testId: "rtr_run_reaction_j1" },
  // Pogos : P2→P3 « Single-leg pogos » (répétitions, V1.13 cle_production H7). Une valeur 1
  // vient de l'ancien oui/non (avant le 23/10/2026) et n'est pas lue comme 1 répétition.
  pogos_sl: { from: ["p2→p3_pogos", "rtr_run_pogo_sl_reps"], derive: m => pogoReps(m) },
  bassin_neutre: { testId: "rtr_run_bassin_neutre" },
  quad_lsi: {
    from: ["p2→p3_isok_rtr", "p2→p3_quad_iso", "preop→p1_epic_quad_iso"],
    derive: m => {
      const q = { isok: isokLsi(val(m, "p2→p3_isok_rtr")), microfet: lsiFromPair(val(m, "p2→p3_quad_iso")), epic: epicPct(m, "p2→p3_quad_iso") };
      return Object.values(q).some(x => x !== undefined) ? q : undefined;
    },
  },
  calf_raise_reps: { testId: "p2→p3_sl_calf" },
  olr_reps: { testId: "p2→p3_sl_rise" },
  ikdc: { testId: "p2→p3_ikdc_p2" },
  sl_squat_quality: { testId: "p2→p3_slsq", read: v => (isNum(v) ? CROSSLEY[Number(v)] : undefined) },
  epanchement_p2_exit: { testId: "p2→p3_epanch", read: epanchGrade },
  // Blocs (score de suivi)
  quad_isok_lsi: { testId: "p2→p3_isok_rtr", read: v => isokLsi(v, "quad") },
  quad_epic: { from: ["p2→p3_quad_iso", "preop→p1_epic_quad_iso"], derive: m => epicPct(m, "p2→p3_quad_iso") },
  ham_isok_lsi: { testId: "p2→p3_isok_rtr", read: v => isokLsi(v, "ham") },
  pogo_sl_reps: { from: ["p2→p3_pogos", "rtr_run_pogo_sl_reps"], derive: m => pogoReps(m) },
  hop_vh_tolere: { testId: "rtr_run_hop_vh_tolere" },
  // Cone hop / tuck jump : V1.13 les rattache aux « Repeated forward / vertical hop » de P2→P3
  // (réussi = ≥ 15 répétitions, seuil CRITERIA) ; la saisie dédiée reste lue si plus récente.
  cone_hop_fwd_bwd: { from: ["p2→p3_rep_fwd_hop", "rtr_run_cone_hop"], derive: m => latestOf(m, [["p2→p3_rep_fwd_hop", repsOk], ["rtr_run_cone_hop", bool]]) },
  tuck_jump_sl: { from: ["p2→p3_rep_vert_hop", "rtr_run_tuck_jump_sl"], derive: m => latestOf(m, [["p2→p3_rep_vert_hop", repsOk], ["rtr_run_tuck_jump_sl", bool]]) },
  // RSI drop jump unipodal côté opéré, m/s (My Jump Lab) — V1.15, D6
  dj_rsi_sl: { testId: "rtr_run_dj_rsi_sl" },
  dj_rsi_lsi: { testId: "rtr_run_dj_rsi_lsi" },
  sl_bridge: { testId: "p2→p3_sl_bridge" },
  balance: {
    from: ["p2→p3_bal_side_p2", "p2→p3_bal_updown_p2"],
    derive: m => {
      const a = bool(val(m, "p2→p3_bal_side_p2")), b = bool(val(m, "p2→p3_bal_updown_p2"));
      if (a === false || b === false) return false;
      return a === true && b === true ? true : undefined;
    },
  },
  // Side bridge : V1.15 saisi des deux côtés {h: sain, i: opéré} ; le score lit le temps côté opéré
  // (une ancienne saisie d'un seul nombre reste lue telle quelle).
  side_bridge_time: { testId: "p2→p3_side_bridge", read: v => (v && typeof v === "object" ? num(v.i) : num(v)) },
  pain_next_morning_14j: { testId: "rtr_run_pain_next_morning_14j" },
  rpe_session_stable: {
    from: ["carnet de séances", "rtr_run_rpe_stable"],
    derive: (m, ctx) => rpeStableFromLog(ctx.sessionLog, ctx.date) ?? bool(val(m, "rtr_run_rpe_stable")),
  },
  adherence_pct: {
    from: ["carnet de séances", "rtr_run_freq_prescrite", "rtr_run_adherence_pct"],
    derive: (m, ctx) => adherenceFromLog(ctx.sessionLog, val(m, "rtr_run_freq_prescrite"), ctx.date) ?? num(val(m, "rtr_run_adherence_pct")),
  },
  giving_way: {
    from: ["p2→p3_derob_bilan", "giving_way_events"],
    derive: (m, ctx) => {
      const g = givingWayHurdleValue(m, ctx, "p2→p3");
      if (g.answer === true || g.count > 0) return false;
      return g.answer === false ? true : undefined;
    },
  },
  derobements: {
    from: ["p2→p3_derob_bilan", "giving_way_events"],
    derive: (m, ctx) => givingWayHurdleValue(m, ctx, "p2→p3"),
  },
  acl_rsi: { testId: "p2→p3_aclrsi_p2" },
  tsk11: { testId: "p3→p4_tsk11_p3" },
};

// ── MRSS 2.0 ─────────────────────────────────────────────────────────
// Sources des essais bruts (D2) : clé MRSS → test_id
export const MRSS_TRIAL_SOURCES = {
  sebt: "p3→p4_sebt_p3",
  sh: "p3→p4_single_hop_rts",
  th: "p3→p4_triple_hop_rts",
  ch: "p3→p4_crossover_rts",
  sideh: "p3→p4_sidehop30_rts",
  rise: "p3→p4_sl_rise_p3",
  fsh: "mrss_f_single_hop",
  fth: "mrss_f_triple_hop",
  fch: "mrss_f_crossover_hop",
  fsideh: "mrss_f_side_hop",
};

// Essais bruts d'une ligne : `trials` si saisis dans le MRSS, sinon la paire {h,i}
// saisie dans le scoring de phases est traitée comme un essai unique par côté.
export function trialsOf(valeur) {
  if (valeur?.trials) return valeur.trials;
  const p = valeur?.v;
  if (p && typeof p === "object" && num(p.h) && num(p.i)) return { inj: [num(p.i)], healthy: [num(p.h)] };
  return undefined;
}

// Valeur `v` dérivée des essais, pour que le scoring de phases affiche la même donnée.
export function valueFromTrials(testId, trials, useAvg = false) {
  const pick = arr => {
    const xs = (arr || []).map(num).filter(x => x !== undefined);
    if (!xs.length) return 0;
    return useAvg ? round1(xs.reduce((a, b) => a + b, 0) / xs.length) : Math.max(...xs);
  };
  if (testId === "p3→p4_sl_rise_p3") return pick(trials.inj); // critère CRITERIA = reps côté opéré
  return { h: pick(trials.healthy), i: pick(trials.inj) };
}

const PTS_PIVOT = [0, 1, 3, 5];   // ["Grade III-IV","Grade II","Grade I","Nil"]
const PTS_ROM = [0, 3, 5];        // ["…+ déficit","…","0-… déficit"]
const ptsAt = (table, v) => (isNum(v) ? table[Number(v)] : undefined);

// Délai MRSS / RTR-SPORT : calculé depuis la date d'opération (V1.15, D8)
export function sportDelayMonths(m, ctx) {
  return monthsSinceOp(ctx);
}

export function buildMrssInputs(m = {}, ctx = {}) {
  const trials = {};
  for (const [k, id] of Object.entries(MRSS_TRIAL_SOURCES)) trials[k] = trialsOf(m[id]);
  const ep = val(m, "p3→p4_epanch_p3");
  const delay = sportDelayMonths(m, ctx);
  return {
    ep: isNum(ep) ? (Number(ep) === 3 ? 5 : 0) : undefined,
    piv: ptsAt(PTS_PIVOT, val(m, "p3→p4_pivot_p3")),
    flex: ptsAt(PTS_ROM, val(m, "p3→p4_flex_p3")),
    ext: ptsAt(PTS_ROM, val(m, "p3→p4_ext_p3")),
    ikdc: num(val(m, "p3→p4_ikdc_p3")),
    rsi: num(val(m, "p3→p4_aclrsi_p3")),
    tsk: num(val(m, "p3→p4_tsk11_p3")),
    dom: bool(val(m, "mrss_dominance")) === true,
    trials,
    vest_lat: bool(val(m, "p3→p4_bal_side_p3")),
    vest_hb: bool(val(m, "p3→p4_bal_updown_p3")),
    e1: bool(val(m, "mrss_e1")),
    e2: bool(val(m, "mrss_e2")),
    prevLCA: bool(val(m, "rtr_sport_prevention")),
    prevDelai: delay === undefined ? undefined : delay >= 9,
  };
}

// MRSS calculé sur les seules mesures fraîches (une mesure périmée = non mesurée)
export function computeMrssFromMeasures(m, ctx = {}) {
  const src = ctx.skipFreshness ? m : splitFresh(m, ctx.date).fresh;
  return computeMrss(buildMrssInputs(src, ctx));
}

// ── RTR-SPORT (gate T6, P4→RTS) ───────────────────────────────────────
export const RTR_SPORT_MAP = {
  // Délai : toujours calculé depuis la date d'opération (V1.15, D8), en mois complets
  delai_postop: { from: ["date d'opération"], derive: (m, ctx) => monthsSinceOp(ctx), auto: true },
  tsk11: { testId: "p3→p4_tsk11_p3" },
  epanchement: { testId: "p3→p4_epanch_p3", read: epanchGrade },
  rom: { testId: "rtr_sport_rom" },
  mrss: {
    from: ["MRSS 2.0"],
    derive: (m, ctx) => {
      const r = computeMrssFromMeasures(m, ctx);
      return r.complete ? Math.round(r.total * 10) / 10 : undefined;
    },
  },
  condition_generale: {
    from: ["mrss_e1", "mrss_e2"],
    derive: m => {
      const a = bool(val(m, "mrss_e1")), b = bool(val(m, "mrss_e2"));
      if (a === false || b === false) return false;
      return a === true && b === true ? true : undefined;
    },
  },
  prevention: { testId: "rtr_sport_prevention" },
  accord_chirurgien: { testId: "rtr_sport_accord_chirurgien" },
  derobements: {
    from: ["p3→p4_derob_bilan", "giving_way_events"],
    derive: (m, ctx) => givingWayHurdleValue(m, ctx, "p3→p4"),
  },
  symetrie_lsi: {
    from: ["p3→p4_isok_rts", "p3→p4_single_hop_rts", "p3→p4_triple_hop_rts", "p3→p4_crossover_rts", "p3→p4_sidehop30_rts", "p3→p4_vertical_hop_rts"],
    derive: m => minSymmetry(m)?.lsi ?? num(val(m, "rtr_sport_symetrie_lsi")),
  },
  cod: { testId: "rtr_sport_cod" },
  conditioning: { testId: "rtr_sport_conditioning" },
  sports_specific: { testId: "rtr_sport_sports_specific" },
  motor_control: { testId: "rtr_sport_motor_control" },
  hip_abd: { from: ["rtr_sport_hip_abd", "preop→p1_epic_hip_abd"], derive: m => withPreopEpic(m, "rtr_sport_hip_abd", "preop→p1_epic_hip_abd") },
  hip_add: { from: ["rtr_sport_hip_add", "preop→p1_epic_hip_add"], derive: m => withPreopEpic(m, "rtr_sport_hip_add", "preop→p1_epic_hip_add") },
  ratio_add_abd: { testId: "rtr_sport_ratio_add_abd" },
  hip_re: { from: ["rtr_sport_hip_re", "preop→p1_epic_hip_re"], derive: m => withPreopEpic(m, "rtr_sport_hip_re", "preop→p1_epic_hip_re") },
  calf_mvc: { from: ["rtr_sport_calf_mvc", "preop→p1_epic_calf"], derive: m => withPreopEpic(m, "rtr_sport_calf_mvc", "preop→p1_epic_calf") },
  quad_isok: { from: ["p3→p4_isok_rts"], derive: m => isokSymmetric("quad")(m) ?? val(m, "rtr_sport_quad_isok") },
  ham_isok: { from: ["p3→p4_isok_rts"], derive: m => isokSymmetric("ham")(m) ?? val(m, "rtr_sport_ham_isok") },
  cmj_height: { from: ["p3→p4_vertical_hop_rts", "preop→p1_epic_sl_cmj", "rtr_sport_cmj_height"], derive: m => vsPreopBaseline(m, "cmj")?.meets ?? val(m, "rtr_sport_cmj_height") },
  sl_hop_distance: { from: ["p3→p4_single_hop_rts", "preop→p1_hop_preop", "rtr_sport_sl_hop_distance"], derive: m => vsPreopBaseline(m, "hop")?.meets ?? val(m, "rtr_sport_sl_hop_distance") },
  drop_jump: { testId: "rtr_sport_drop_jump" },
  contact_time: { testId: "rtr_sport_contact_time" },
  rsi: { testId: "rtr_sport_rsi" },
  running_biomeca: { testId: "rtr_sport_running_biomeca" },
};

// Résolution générique : measures (test_results) → objet plat attendu par rtrEngine
export function resolveMeasurements(map, m = {}, ctx = {}) {
  const out = {};
  for (const [key, src] of Object.entries(map)) {
    let v;
    if (src.derive) v = src.derive(m, ctx);
    else {
      const raw = val(m, src.testId);
      v = src.read ? (raw === undefined ? undefined : src.read(raw)) : raw;
    }
    if (v === undefined && src.fallback) v = src.fallback(ctx);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

// Pour l'UI : une variable est saisissable dans le panneau RTR seulement si
// elle lit une ligne telle quelle (pas de conversion, pas de dérivation).
export function rtrSourceInfo(map, key) {
  const src = map[key];
  if (!src) return null;
  return {
    testId: src.testId || null,
    editable: !!src.testId && !src.read && !src.derive,
    from: src.from || (src.testId ? [src.testId] : []),
    fallbackLabel: src.fallbackLabel || null,
  };
}

// Résolution avec fraîcheur : une variable moteur est calculée sur les mesures fraîches ;
// si elle n'existe qu'avec des mesures périmées, elle est omise et listée dans `stale`
// (rtrEngine → NON_MESURE pour le score, A_RETESTER pour un hurdle).
export function resolveWithFreshness(map, m = {}, ctx = {}) {
  const { fresh, stale: staleTests } = splitFresh(m, ctx.date);
  const rawCtx = { ...ctx, skipFreshness: true }; // le filtrage est fait ici, pas dans les dérivations
  const measurements = resolveMeasurements(map, fresh, rawCtx);
  const all = resolveMeasurements(map, m, rawCtx);
  const stale = {};
  for (const key of Object.keys(all)) {
    if (key in measurements) continue;
    const src = map[key];
    const ids = src.testId ? [src.testId] : (src.from || []);
    const st = ids.map(id => staleTests[id]).filter(Boolean).sort((a, b) => b.ageDays - a.ageDays)[0]
      || Object.values(staleTests).sort((a, b) => b.ageDays - a.ageDays)[0]; // dérivé (ex. MRSS) : mesure la plus ancienne
    if (st) stale[key] = st;
  }
  return { measurements, stale, staleTests };
}

export const buildRtrRunMeasurements = (m, ctx) => resolveWithFreshness(RTR_RUN_MAP, m, ctx).measurements;
export const buildRtrSportMeasurements = (m, ctx) => resolveWithFreshness(RTR_SPORT_MAP, m, ctx).measurements;
export const buildRtrRunInput = (m, ctx) => resolveWithFreshness(RTR_RUN_MAP, m, ctx);
export const buildRtrSportInput = (m, ctx) => resolveWithFreshness(RTR_SPORT_MAP, m, ctx);
